import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import Popover from 'components/popover/Popover';
import NetworkGraph from 'components/graphs/network-graph/NetworkGraphLazy';
import { withModal } from 'components/modal/ModalHOC';

import InfluencerCreatorNetworkList from './list/InfluencerCreatorNetworkList';

import { fetchNotableFollowers } from './reducers/influencer-creator-network';

import { __t, applyTranslations } from 'utils/language';
import { getInfluencerFullName } from 'utils/influencers';
import { getData } from 'components/graphs/network-graph/utils/network-graph';

import './influencer-creator-network.scss';

import type {
  InfluencerProfile,
  NetworkGraphDataRaw
} from 'types';

applyTranslations(require('./influencer-creator-network.i18n.json'));

type OwnProps = {
  influencerProfile: InfluencerProfile,
  isFreemium: boolean | null | undefined
};

type StateProps = {
  creatorNetwork: NetworkGraphDataRaw
};

type DispatchProps = {
  dispatchFetchFollowers: (a: number) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isOverlayVisible: boolean
};

const ActionButtonWithListModal = withModal(ActionButton, {
  modalContent: InfluencerCreatorNetworkList,
  width: 'medium',
  positionOverride: 'auto-centered'
});

const OVERLAY_INTERVAL_TIME = 500;
let OVERLAY_INTERVAL_ID: number | undefined;

const MSG_KEY = 'influencer_creator_network';

class InfluencerCreatorNetwork extends React.PureComponent<Props, State> {
  state = {
    isOverlayVisible: true
  };

  componentDidMount() {
    const { influencerProfile, dispatchFetchFollowers } = this.props;

    dispatchFetchFollowers(influencerProfile.id);
  }

  componentWillUnmount() {
    this.handleClearInterval();
  }

  getInfluencerName(): string {
    const { influencerProfile } = this.props;

    return influencerProfile.artistName
      || getInfluencerFullName(influencerProfile)
      || influencerProfile.name
      || __t(`${MSG_KEY}.labels.creator`);
  }

  handleClearInterval = (): void => {
    clearInterval(OVERLAY_INTERVAL_ID);
  };

  handleMouseEnter = (): void => {
    const { isOverlayVisible } = this.state;

    // once overlay is hidden, we no longer need to worry about it
    if (!isOverlayVisible) {
      return;
    }

    // @ts-ignore
    OVERLAY_INTERVAL_ID = setInterval(() => {
      this.setState({
        isOverlayVisible: false
      });

      this.handleClearInterval();
    }, OVERLAY_INTERVAL_TIME);
  };

  renderListModalButton(): React.ReactNode {
    const { influencerProfile } = this.props;

    return (
      <div className="influencer-creator-network__list-modal-button">
        <ActionButtonWithListModal
          modalContentProps={{
            influencerProfile
          }}
        >
          {__t(`${MSG_KEY}.buttons.view_list`)}
        </ActionButtonWithListModal>
      </div>
    );
  }

  renderPopover(): React.ReactNode {
    return (
      <Popover showQuestionMark>
        <div className="influencer-creator-network__title-popover-contents">
          <div className="influencer-creator-network__title-popover-contents-emphasis">
            {this.getInfluencerName()}&nbsp;
          </div>
          {__t(`${MSG_KEY}.tooltips.creator_network`)}
        </div>
      </Popover>
    );
  }

  renderTitle(): React.ReactNode {
    return (
      <div className="influencer-creator-network__title-container">
        <div className="influencer-creator-network__title">
          <div className="influencer-creator-network__title-label">
            {__t(`${MSG_KEY}.labels.creator_network`)}
          </div>
          <div className="influencer-creator-network__title-popover">
            {this.renderPopover()}
          </div>
        </div>
        {this.renderSubtitle()}
      </div>
    );
  }

  renderSubtitle(): React.ReactNode {
    return (
      <div className="influencer-creator-network__subtitle">
        <div className="influencer-creator-network__subtitle-main">
          {__t(`${MSG_KEY}.messages.subtitle_main`)}
        </div>
        <div>
          {__t(`${MSG_KEY}.messages.subtitle_sub`)}
        </div>
      </div>
    );
  }

  renderGraphOverlay(): React.ReactNode {
    return (
      <div
        className="influencer-creator-network__graph-overlay"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleClearInterval}
      />
    );
  }

  renderGraph(): React.ReactNode {
    const { creatorNetwork, isFreemium } = this.props;
    const { isOverlayVisible } = this.state;

    return (
      <div className="influencer-creator-network__graph">
        <NetworkGraph
          key={`influencer-creator-network__graph--${creatorNetwork.nodes.length}-${creatorNetwork.links.length}`}
          data={getData(creatorNetwork)}
        />
        {
          (isOverlayVisible || isFreemium) &&
            this.renderGraphOverlay()
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { isFreemium } = this.props;

    return (
      <div className="influencer-creator-network">
        <div className="influencer-creator-network__header">
          {this.renderTitle()}
          {
            !isFreemium &&
              this.renderListModalButton()
          }
        </div>
        {this.renderGraph()}
      </div>
    );
  }
}

const mapStateToProps = ({ influencerCreatorNetwork }): StateProps => ({
  creatorNetwork: influencerCreatorNetwork.followers
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchFollowers: (influencerProfileId: number) => (
    dispatch(fetchNotableFollowers(influencerProfileId))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerCreatorNetwork);
